import type { HTMLAudioElementWithSetSinkId } from '@/entities/media-devices'

import { tauriMode } from '@/shared/utils/import-meta'
import { isTypedKeyInObject } from '@/shared/utils/isTypedKeyInObject'
import { isLinuxOrUnixPlatform } from '@/shared/utils/tauri-platform'

import { CustomNavigator } from './use-app-badge'

/** Web Notifications API is missing in iOS Safari browser tabs (available in installed PWA). */
export const isWebNotificationApiAvailable = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window
}

const hasSetSinkIdSupport = (): boolean => {
  if (typeof HTMLMediaElement === 'undefined') {
    return false
  }

  return typeof (HTMLMediaElement.prototype as unknown as HTMLAudioElementWithSetSinkId).setSinkId === 'function'
}

const hasSelectAudioOutputSupport = (): boolean => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return false
  }

  return typeof (navigator.mediaDevices as MediaDevices & {
    selectAudioOutput?: unknown
  }).selectAudioOutput === 'function'
}

export const useBrowserSupport = () => {
  const canUseSetAudioOutput = async (mediaElement: HTMLAudioElementWithSetSinkId) => {
    if (tauriMode() && await isLinuxOrUnixPlatform()) {
      return true
    }
    return isTypedKeyInObject(mediaElement, 'setSinkId', 'function')
  }
  const canUseAppBadge = () => {
    return isTypedKeyInObject(navigator as CustomNavigator, 'setAppBadge', 'function')
  }

  const canUseClearAppBadge = () => {
    return isTypedKeyInObject(navigator as CustomNavigator, 'clearAppBadge', 'function')
  }

  /** Feature-detect sink switching; no UA ban for Firefox 150+. */
  const canUseSpeakersChange = () => hasSetSinkIdSupport() || hasSelectAudioOutputSupport()

  const canUseSelectAudioOutput = () => hasSelectAudioOutputSupport()

  const canUseWindowLocationSearch = (): boolean => {
    return !!window.location.search && typeof window.location.search === 'string'
  }

  const canUseLaunchQueue = (): boolean => {
    return 'launchQueue' in window
  }

  const canUseWebNotifications = (): boolean => isWebNotificationApiAvailable()

  return {
    canUseAppBadge,
    canUseClearAppBadge,
    canUseLaunchQueue,
    canUseWebNotifications,
    canUseSetAudioOutput,
    canUseSelectAudioOutput,
    canUseSpeakersChange,
    canUseWindowLocationSearch,
  }
}
